import api from '../../lib/api';
import type {
  CalibrationRecord,
  CalibrationRecordPayload,
  Equipment,
  EquipmentListParams,
  EquipmentListResponse,
  EquipmentPayload,
  MaintenanceAlertPreview,
  MaintenanceOrder,
  MaintenanceOrderPayload,
  MaintenancePlan,
  MaintenancePlanPayload,
  MaintenanceRecord,
  MaintenanceRecordPayload,
  MaintenanceOrderStatus,
  MaintenanceReportParams,
  Pagination,
  PreventiveVsCorrectiveReport,
} from './types';

export const fetchEquipmentList = async (params: EquipmentListParams = {}): Promise<EquipmentListResponse> => {
  const { data } = await api.get<EquipmentListResponse>('maintenance/equipos', { params });
  return data;
};

export const fetchEquipment = async (id: string): Promise<Equipment> => {
  const { data } = await api.get<Equipment>(`maintenance/equipos/${id}`);
  return data;
};

export const createEquipment = async (payload: EquipmentPayload): Promise<Equipment> => {
  const { data } = await api.post<Equipment>('maintenance/equipos', payload);
  return data;
};

export const updateEquipment = async (id: string, payload: EquipmentPayload): Promise<Equipment> => {
  const { data } = await api.put<Equipment>(`maintenance/equipos/${id}`, payload);
  return data;
};

export const deactivateEquipment = async (id: string): Promise<Equipment> => {
  const { data } = await api.delete<Equipment>(`maintenance/equipos/${id}`);
  return data;
};

export const fetchPlans = async (equipmentId: string): Promise<MaintenancePlan[]> => {
  const { data } = await api.get<MaintenancePlan[]>(`maintenance/equipos/${equipmentId}/planes`);
  return data;
};

export const createPlan = async (
  equipmentId: string,
  payload: MaintenancePlanPayload,
): Promise<MaintenancePlan> => {
  const { data } = await api.post<MaintenancePlan>(`maintenance/equipos/${equipmentId}/planes`, payload);
  return data;
};

export const updatePlan = async (
  equipmentId: string,
  planId: string,
  payload: MaintenancePlanPayload,
): Promise<MaintenancePlan> => {
  const { data } = await api.put<MaintenancePlan>(
    `maintenance/equipos/${equipmentId}/planes/${planId}`,
    payload,
  );
  return data;
};

export const fetchAlertsPreview = async (): Promise<MaintenanceAlertPreview> => {
  const { data } = await api.get<MaintenanceAlertPreview>('maintenance/alerts/preview');
  return data;
};

export const fetchPreventiveVsCorrective = async (
  params: MaintenanceReportParams = {},
): Promise<PreventiveVsCorrectiveReport> => {
  const { data } = await api.get<PreventiveVsCorrectiveReport>(
    'maintenance/reports/preventive-vs-corrective',
    { params },
  );
  return data;
};

interface OrderListParams {
  equipmentId?: string;
  planId?: string;
  status?: MaintenanceOrderStatus;
  page?: number;
  pageSize?: number;
}
interface OrderListResponse {
  data: MaintenanceOrder[];
  pagination: Pagination;
}

export const fetchOrders = async (params: OrderListParams = {}): Promise<OrderListResponse> => {
  const { data } = await api.get<OrderListResponse>('maintenance/ordenes', { params });
  return data;
};

export const createOrder = async (payload: MaintenanceOrderPayload): Promise<MaintenanceOrder> => {
  const { data } = await api.post<MaintenanceOrder>('maintenance/ordenes', payload);
  return data;
};

export const completeOrder = async (id: string, body: { executedAt?: string; resultNotes?: string | null; cost?: number | null } = {}): Promise<MaintenanceOrder> => {
  const { data } = await api.post<MaintenanceOrder>(`maintenance/ordenes/${id}/completar`, body);
  return data;
};

export const cancelOrder = async (id: string): Promise<MaintenanceOrder> => {
  const { data } = await api.post<MaintenanceOrder>(`maintenance/ordenes/${id}/cancelar`, {});
  return data;
};

export const fetchRecords = async (orderId?: string): Promise<MaintenanceRecord[]> => {
  const { data } = await api.get<MaintenanceRecord[]>('maintenance/registros', { params: { orderId } });
  return data;
};

export const createRecord = async (payload: MaintenanceRecordPayload): Promise<MaintenanceRecord> => {
  const { data } = await api.post<MaintenanceRecord>('maintenance/registros', payload);
  return data;
};

export const fetchCalibrationRecords = async (equipmentId: string): Promise<CalibrationRecord[]> => {
  const { data } = await api.get<CalibrationRecord[]>(`maintenance/equipos/${equipmentId}/calibraciones`);
  return data;
};

export const createCalibrationRecord = async (
  equipmentId: string,
  payload: CalibrationRecordPayload,
): Promise<CalibrationRecord> => {
  const { data } = await api.post<CalibrationRecord>(
    `maintenance/equipos/${equipmentId}/calibraciones`,
    payload,
  );
  return data;
};
